import React, { type ReactElement } from 'react';
import type { SortingState } from '@tanstack/react-table';
import { mapValues } from 'lodash';
import {
  DelimitedArrayParam,
  DelimitedNumericArrayParam,
  StringParam,
  useQueryParam,
  useQueryParams,
  withDefault
} from 'use-query-params';
import Card from 'atom-components/card';
import TablePagination from 'atom-components/table-pagination';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import { markableResponseStatuses } from 'helpers/domain-specific/response-status.helpers';
import {
  defaultPage,
  defaultPaginationFilters,
  generatePaginationFilters
} from 'helpers/pagination.helpers';
import { toArrayOfNumbers, toArrayOfStrings } from 'helpers/query-params.helpers';
import {
  defaultSortingFilter,
  fromSortingState,
  isColumnSorted,
  sortDirection,
  toSortingState
} from 'helpers/sorting.helpers';
import { firstTimeInYear, lastTimeInYear } from 'helpers/year.helpers';
import MarkingPageLayout from 'pages/marking/layouts/marking-page-layout';
import FilterForm from 'pages/marking/subpages/marking-allocation-tab/filter-form';
import { type FilterFormValues } from 'pages/marking/subpages/marking-allocation-tab/filter-form/filter-form.schema';
import { commonNumberInputMax, commonNumberInputMin } from 'schemas/number.schema';
import { useAreasWithNoDefaults } from 'services/assist-api/entities/area/areas';
import type { BaseArea } from 'services/assist-api/entities/area/areas';
import { useMarkerAllocation } from 'services/assist-api/entities/test-instance-response/marker-allocation/hooks';
import type { TestInstanceResponseInputType } from 'services/assist-api/entities/test-instance-response/types';
import { useAuthCustomer } from 'services/providers/auth.hooks';
import { isInitialLoad } from 'utils/check-query-status';
import MarkingAllocationTable from './marking-allocation-table';

const defaultTableFilters = {
  nsn: withDefault(StringParam, ''),
  responseId: withDefault(StringParam, ''),
  schoolGroupName: withDefault(StringParam, ''),
  testIds: withDefault(DelimitedNumericArrayParam, []),
  assignmentName: withDefault(StringParam, ''),
  modes: withDefault(DelimitedArrayParam, []),
  numberOfMarkersFrom: withDefault(StringParam, commonNumberInputMin.toString()),
  numberOfMarkersTo: withDefault(StringParam, commonNumberInputMax.toString()),
  markerOrganisationUserIds: withDefault(DelimitedNumericArrayParam, []),
  numberOfMarksFrom: withDefault(StringParam, commonNumberInputMin.toString()),
  numberOfMarksTo: withDefault(StringParam, commonNumberInputMax.toString())
};

export default function MarkingAllocationTab(): ReactElement {
  const [tableFilters, setTableFilters] = useQueryParams(defaultTableFilters);
  const [sortingFilter, setSortingFilter] = useQueryParam('sorting', defaultSortingFilter);
  const [paginationFilters, setPaginationFilters] = useQueryParams(defaultPaginationFilters);

  // Get the current user's organisation from the cache. We should have an earlier check to ensure
  // that the user has joined and is switched to an organisation by this point:
  const currentOrganisation = useAuthCustomer();

  const areasQuery = useAreasWithNoDefaults(
    { filters: { markingEnabled: true } },
    { enabled: true }
  );

  function generateMarkerAllocationFilters(): EntityFilters {
    const filters: EntityFilters = {};

    // Start table row filters.
    if (tableFilters.nsn) {
      filters['user.user.identifier'] = tableFilters.nsn;
    }
    if (tableFilters.responseId) {
      filters.id = tableFilters.responseId;
    }
    if (tableFilters.schoolGroupName) {
      filters['user.user.groups.group.name'] = tableFilters.schoolGroupName;
    }
    if (tableFilters.testIds.length > 0) {
      filters['testInstance.test'] = toArrayOfNumbers(tableFilters.testIds);
    }
    if (tableFilters.assignmentName) {
      filters['testInstance.name'] = tableFilters.assignmentName;
    }
    if (tableFilters.modes.length > 0) {
      filters.inputTypes = toArrayOfStrings(tableFilters.modes);
    }
    if (tableFilters.numberOfMarkersFrom) {
      filters['responseMarkers[gte]'] = tableFilters.numberOfMarkersFrom;
    }
    if (tableFilters.numberOfMarkersTo) {
      filters['responseMarkers[lte]'] = tableFilters.numberOfMarkersTo;
    }
    if (tableFilters.markerOrganisationUserIds.length > 0) {
      filters['responseMarkers.user'] = toArrayOfNumbers(tableFilters.markerOrganisationUserIds);
    }
    if (tableFilters.numberOfMarksFrom) {
      filters['responseMarks[gte]'] = tableFilters.numberOfMarksFrom;
    }
    if (tableFilters.numberOfMarksTo) {
      filters['responseMarks[lte]'] = tableFilters.numberOfMarksTo;
    }
    // End table row filters.

    // Start column sorting filters.
    if (isColumnSorted('nsn', sortingFilter)) {
      filters['order[user.user.identifier]'] = sortDirection('nsn', sortingFilter);
    }
    if (isColumnSorted('responseId', sortingFilter)) {
      filters['order[id]'] = sortDirection('responseId', sortingFilter);
    }
    if (isColumnSorted('schools', sortingFilter)) {
      filters['order[user.user.groups.group.name]'] = sortDirection('schools', sortingFilter);
    }
    if (isColumnSorted('test', sortingFilter)) {
      filters['order[testInstance.test.name]'] = sortDirection('test', sortingFilter);
    }
    if (isColumnSorted('assignment', sortingFilter)) {
      filters['order[testInstance.name]'] = sortDirection('assignment', sortingFilter);
    }
    if (isColumnSorted('mode', sortingFilter)) {
      filters['order[inputTypes]'] = sortDirection('mode', sortingFilter);
    }
    if (isColumnSorted('numberOfMarkers', sortingFilter)) {
      filters['order[responseMarkers]'] = sortDirection('numberOfMarkers', sortingFilter);
    }
    // Note: column sorting is disabled for the Markers column for now. From Jarrod: gets
    // complicated when there are multiple markers assigned to each response etc.
    if (isColumnSorted('numberOfMarks', sortingFilter)) {
      filters['order[responseMarks]'] = sortDirection('numberOfMarks', sortingFilter);
    }
    // End column sorting filters.

    return filters;
  }

  const currentYear = new Date().getFullYear().toString();
  const markerAllocationQuery = useMarkerAllocation(
    {
      filters: {
        'testInstance.customer': currentOrganisation?.id,
        'testInstance.area': areasQuery.data?.json.map((area: BaseArea) => area.id),
        'testInstance.startDate[after]': firstTimeInYear(currentYear),
        'testInstance.startDate[before]': lastTimeInYear(currentYear),
        status: markableResponseStatuses,
        ...generatePaginationFilters(paginationFilters.pageSize, paginationFilters.page),
        ...generateMarkerAllocationFilters()
      }
    },
    { enabled: !!(paginationFilters?.pageSize && paginationFilters?.page) }
  );

  const isLoading =
    !tableFilters || !currentOrganisation || isInitialLoad(areasQuery, markerAllocationQuery);

  const isError = areasQuery.isError || markerAllocationQuery.isError;

  return (
    <MarkingPageLayout>
      <Card>
        <Card.Header>
          <Card.Heading level={3}>Current Responses</Card.Heading>
        </Card.Header>
        <Card.Body>
          <FilterForm
            defaultValuesFromURLParams={{
              nsn: tableFilters.nsn,
              responseId: tableFilters.responseId,
              schoolGroupName: tableFilters.schoolGroupName,
              testIds: toArrayOfNumbers(tableFilters.testIds),
              assignmentName: tableFilters.assignmentName,
              modes: toArrayOfStrings(tableFilters.modes) as TestInstanceResponseInputType[],
              numberOfMarkersFrom: tableFilters.numberOfMarkersFrom,
              numberOfMarkersTo: tableFilters.numberOfMarkersTo,
              markerOrganisationUserIds: toArrayOfNumbers(tableFilters.markerOrganisationUserIds),
              numberOfMarksFrom: tableFilters.numberOfMarksFrom,
              numberOfMarksTo: tableFilters.numberOfMarksTo
            }}
            onSubmit={(formValues: FilterFormValues): void => {
              setTableFilters({ ...formValues });
              setPaginationFilters({ page: defaultPage });
            }}
            onReset={(): void => {
              setTableFilters(mapValues(defaultTableFilters, () => undefined));
              setPaginationFilters({ page: defaultPage });
            }}
          />
          {!isLoading && isError && (
            <LoadingErrorAlert queries={[areasQuery, markerAllocationQuery]} />
          )}
          <MarkingAllocationTable
            isLoading={isLoading}
            markerAllocation={markerAllocationQuery.data?.json || []}
            onRequestRefetch={async (): Promise<void> => {
              await markerAllocationQuery.refetch();
            }}
            currentOrganisation={currentOrganisation}
            initialSorting={toSortingState(sortingFilter)}
            onSort={(sorting: SortingState): void => setSortingFilter(fromSortingState(sorting))}
          />
        </Card.Body>
        <Card.Footer>
          <TablePagination
            query={markerAllocationQuery}
            onChange={({ pageSize, page }): void => setPaginationFilters({ pageSize, page })}
          />
        </Card.Footer>
      </Card>
    </MarkingPageLayout>
  );
}
